import { Card } from "react-bootstrap";
import Button from "app/storybookComponents/Button";
import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getTextSynthesis,
  getWordCloud,
  getTextInsights,
  getFeedbackResponsesCount,
  selectTextSynthesis,
  selectTextInsights,
  selectGetTextSynthesisStatus,
  selectTextInsightsErrorMessage,
  selectFeedbackResponsesCount,
  selectGetFeedbackResponsesCountStatus,
  resetTextInsights,
  resetTextSynthesis,
  selectAiFeaturesEnabled,
  resetTeamScanFeedback,
  getTEAMscanFeedbackResponses,
} from "./slice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FreeTextInsightsModal from "./Modals/FreeTextInsightsModal";
import Loading from "app/storybookComponents/Loading";
import { getOpenAIUrlParams } from "./helpers";
import { InstanceShowing } from "app/components/InstancePicker/types";
import EmptyCard from "app/storybookComponents/Cards/EmptyCard";

interface Props {
  departmentId?: number;
  teamId?: number;
  title?: string;
  description?: string;
  hideAIButton?: boolean;
  onShowFreeTextInsightsModal: (bool: boolean) => void;
  showFreeTextInsightsModal: boolean;
  instanceShowing: InstanceShowing;
}
export default function FreeTextSummary({
  departmentId,
  teamId,
  title = "Free Text Summary",
  description = "Analyze the most common words and phrases found in responses and generate insights powered by AI.",
  hideAIButton = false,
  onShowFreeTextInsightsModal,
  showFreeTextInsightsModal,
  instanceShowing,
}: Readonly<Props>) {
  const dispatch = useAppDispatch();
  const textSynthesis = useAppSelector(selectTextSynthesis);
  const textInsights = useAppSelector(selectTextInsights);
  const textInsightsErrorMessage = useAppSelector(
    selectTextInsightsErrorMessage
  );
  const textSynthesisStatus = useAppSelector(selectGetTextSynthesisStatus);
  const feedbackResponsesCount = useAppSelector(selectFeedbackResponsesCount);
  const feedbackResponsesCountStatus = useAppSelector(
    selectGetFeedbackResponsesCountStatus
  );
  const aiFeaturesEnabled = useAppSelector(selectAiFeaturesEnabled);
  const [showAIThemes, setShowAIThemes] = useState(false);

  useEffect(() => {
    dispatch(getWordCloud());
  }, [dispatch]);

  useEffect(() => {
    dispatch(resetTextSynthesis());
    dispatch(resetTextInsights());
    dispatch(resetTeamScanFeedback());

    const payload = {
      ...getOpenAIUrlParams(departmentId, teamId),
    };

    if (
      instanceShowing?.instance ||
      instanceShowing?.startDate ||
      instanceShowing?.endDate
    ) {
      payload.instance = instanceShowing.instance ?? undefined;
      payload.startDate = instanceShowing.startDate ?? undefined;
      payload.endDate = instanceShowing.endDate ?? undefined;
    }

    dispatch(getFeedbackResponsesCount(payload));
    dispatch(getTEAMscanFeedbackResponses(payload));
  }, [
    teamId,
    departmentId,
    dispatch,
    instanceShowing?.instance,
    instanceShowing?.startDate,
    instanceShowing?.endDate,
  ]);

  const getSeeAllCommentsButton = () => (
    <Button
      onClick={() => onShowFreeTextInsightsModal?.(true)}
      variant="secondary-blue"
    >
      See all comments <FontAwesomeIcon icon="arrow-right" className="ms-1" />
    </Button>
  );

  const getAIButton = () => {
    if (hideAIButton) {
      return null;
    }

    if (textSynthesisStatus !== "succeeded") {
      return (
        <Button
          onClick={() => {
            const payload = getOpenAIUrlParams(departmentId, teamId);
            if (instanceShowing) {
              payload.instance = instanceShowing.instance ?? undefined;
              payload.startDate = instanceShowing.startDate ?? undefined;
              payload.endDate = instanceShowing.endDate ?? undefined;
            }

            dispatch(getTextInsights(payload));
            dispatch(getTextSynthesis(payload));
          }}
          disabled={textSynthesisStatus === "loading"}
        >
          Generate Synthesis
        </Button>
      );
    }

    return (
      <Button
        onClick={() => {
          setShowAIThemes(true);
          onShowFreeTextInsightsModal?.(true);
        }}
      >
        See AI generated themes
      </Button>
    );
  };

  const getButtonRow = () => {
    // If AI features are disabled, only show the "See all comments" button
    if (!aiFeaturesEnabled) {
      return getSeeAllCommentsButton();
    }

    if (textSynthesisStatus === "failed") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button disabled>Generate Insights</Button>
          <FontAwesomeIcon icon={["far", "triangle-exclamation"]} />
          <div>{textInsightsErrorMessage}</div>
        </div>
      );
    }

    return (
      <div className="row-gap-12px">
        {getAIButton()}
        {getSeeAllCommentsButton()}
      </div>
    );
  };
  const getFreeTextInsightsModalTitle = () => {
    if (teamId) {
      return "Comments By Your Team";
    }
    if (departmentId) {
      return "Comments By Your Department";
    }
    return "Comments By Your Organization";
  };

  const getEntityType = () => {
    if (teamId) {
      return "team";
    }
    if (departmentId) {
      return "department";
    }
    return "organization";
  };

  const getThemeText = () => {
    if (typeof textSynthesis?.themeText === "string") {
      return textSynthesis?.themeText;
    }
    return null;
  };

  const getSnapshotBox = () => {
    if (!textSynthesis || !textInsights || !aiFeaturesEnabled) {
      return null;
    }

    const themeText = getThemeText();

    if (!themeText) {
      return (
        <div className="warning-banner align-items-center red row-gap-8px">
          <FontAwesomeIcon icon={["far", "triangle-exclamation"]} />
          <p>
            We were unable to generate a synthesis at this time. Please try
            again shortly or contact support if the issue persists.
          </p>
        </div>
      );
    }

    return (
      <div className="snapshot-box">
        <FontAwesomeIcon icon={["far", "lightbulb-on"]} />
        <p>Synthesis: {themeText}</p>
      </div>
    );
  };

  if (
    feedbackResponsesCountStatus === "loading" ||
    !feedbackResponsesCount ||
    feedbackResponsesCount < 2
  ) {
    return (
      <Card className="p-4 column-gap-20px">
        <div className="column-gap-8px">
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <EmptyCard
          title="No feedback responses visible yet"
          bodyText="Once 2 or more responses are added, you'll be able to see comments and generate insights/themes."
        />
      </Card>
    );
  }

  return (
    <>
      <FreeTextInsightsModal
        textInsights={textInsights}
        show={!!showFreeTextInsightsModal}
        entityType={getEntityType()}
        onHide={() => {
          onShowFreeTextInsightsModal?.(false);
          setShowAIThemes(false);
        }}
        title={getFreeTextInsightsModalTitle()}
        aiFeaturesEnabled={aiFeaturesEnabled}
        showAIThemes={showAIThemes}
        setShowAIThemes={setShowAIThemes}
        hideDropdown={hideAIButton}
      />
      <Card className="p-4 column-gap-20px">
        <div className="column-gap-8px">
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        {getSnapshotBox()}
        {textSynthesisStatus === "loading" ? <Loading /> : null}
        <div>{getButtonRow()}</div>
      </Card>
    </>
  );
}
